import {
  queryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  InventoryCenter,
  InventoryCenterConfigOptions,
  InventoryCenterSharepointConfigInput,
  KeyMCell,
  KeyMCellUpdate,
  ManufacturingLocation,
  MCell,
  MCellCompletionConfigCreate,
  MCellCompletionConfigDto,
  MCellCollection,
  MCellCompletionConfigUpdate,
  MCellFilter,
  MCellInfo,
  MCellClonePreview,
  MCellCloneCreate,
  MCellCloneResult,
  PlanningDepartment,
} from './types';
import {
  cloneMCells,
  copyMCellCollectionToPrivate,
  createMCell,
  createMCellCollection,
  createMCellCollectionGlobal,
  createMCellCompletionConfig,
  deleteMCell,
  deleteMCellCollection,
  deleteMCellCompletionConfig,
  getInventoryCenterIdsByManufacturingLocation,
  getInventoryCenters,
  getKeyMCells,
  getMCellById,
  getMCellClonePreviews,
  getMCellCollections,
  getMCellCompletionConfigsByMCellId,
  getMCells,
  getNotificationSystem,
  getOtherUsersPrivateMCellCollections,
  getPlanningDepartments,
  getProducingLocationOptions,
  getProducingLocations,
  getProductionLineOptions,
  getWorkCenterOptions,
  getWorkCenterStationOptions,
  renameMCellCollection,
  setInventoryCenterConfigOptions,
  setMCellCollectionGlobal,
  setMCellCollectionPrivate,
  setMCellsOnMCellCollection,
  setNotificationSystem,
  updateInventoryCenterSharepointConfig,
  updateKeyMCell,
  updateMCell,
  updateMCellCompletionConfig,
  updatePlanningDepartment,
} from './api';
import { QueryKeys } from '../../api/queryKeys';
import { REFERENCE_DATA_STALE_TIME } from '../../queryClient';
import { useInvalidatingMutation } from '../../api/useInvalidatingMutation';
import { OptionValue } from '../common/EnhancedSelect';

/**
 * Custom hook to fetch a list of MCells.
 *
 * @param {MCellFilter} [mCellFilter] - Optional. The filter object to be used in the query.
 * @param {boolean} [enabled=true] - Optional. Determines whether the query should be enabled.
 * @returns {UseQueryResult<Array<MCell>>} The result object from `useQuery`.
 */
export const useGetMCellsQuery = (
  mCellFilter?: MCellFilter,
  enabled: boolean = true,
): UseQueryResult<Array<MCell>> => {
  return useQuery({
    queryKey: [QueryKeys.MCELLS, mCellFilter],
    queryFn: () => getMCells(mCellFilter),
    enabled,
  });
};

/**
 * Custom hook to fetch global MCell Collections.
 *
 * @param {boolean} [enabled=true] - Optional. Determines whether the query should be enabled.
 * @returns {UseQueryResult<Array<MCellCollection>>} The result object from `useQuery`.
 */
export const useGlobalMCellCollectionsQuery = (
  enabled: boolean = true,
): UseQueryResult<Array<MCellCollection>> =>
  useMCellCollectionsQuery(enabled, selectGlobalMCellCollections);

const selectGlobalMCellCollections = (collections: MCellCollection[]) =>
  collections.filter((c) => c.isGlobal);

/**
 * Filter of the app-wide MCell list: every MCell, rollups and components included.
 */
export const ALL_MCELLS_FILTER: MCellFilter = {
  includeRollup: true,
  includeComponentMCells: true,
};

/**
 * Query options of the app-wide MCell list, for prefetching or reading it outside components.
 */
export const allMCellsQueryOptions = queryOptions({
  queryKey: [QueryKeys.MCELLS, ALL_MCELLS_FILTER],
  queryFn: () => getMCells(ALL_MCELLS_FILTER),
  staleTime: REFERENCE_DATA_STALE_TIME,
  meta: { errorMessageKey: 'errorFetchingManufacturingLocations' },
});

/**
 * Fetches the app-wide list of MCells, rollups and components included.
 * @param select - Optional selector over the MCells, e.g. {@link filterMCells}.
 */
export const useAllMCellsQuery = <T = MCell[]>(
  select?: (mCells: MCell[]) => T,
) => useQuery({ ...allMCellsQueryOptions, select });

/**
 * Returns a filter over MCells (usable as a `select`) that drops rollups unless included and
 * keeps one inventory center when given.
 */
export const filterMCells =
  (includeRollupCells: boolean, inventoryCenterId?: string) =>
  (mCells: MCell[]) =>
    mCells.filter(
      (m) =>
        (inventoryCenterId === undefined ||
          m.inventoryCenterId === inventoryCenterId) &&
        (includeRollupCells || !m.isRollup),
    );

/**
 * Query options of the current user's MCell collections, for prefetching or reading them outside components.
 */
export const mCellCollectionsQueryOptions = queryOptions({
  queryKey: [QueryKeys.MCELL_COLLECTIONS],
  queryFn: getMCellCollections,
  staleTime: REFERENCE_DATA_STALE_TIME,
  meta: { errorMessageKey: 'errorFetchingManufacturingLocations' },
});

/**
 * Fetches the MCell collections visible to the current user (their own and global ones).
 * @param enabled - Whether the query is enabled. Defaults to true.
 * @param select - Optional selector over the collections.
 */
export const useMCellCollectionsQuery = <T = MCellCollection[]>(
  enabled: boolean = true,
  select?: (collections: MCellCollection[]) => T,
) => useQuery({ ...mCellCollectionsQueryOptions, enabled, select });

/**
 * Fetches other users' private MCell collections (needs the WorkCellGroupViewAll permission).
 * Its key sits under MCELL_COLLECTIONS, so invalidating that key refreshes both lists.
 * @param enabled - Whether the query is enabled. Defaults to true.
 */
export const useOtherUsersPrivateMCellCollectionsQuery = (
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: [QueryKeys.MCELL_COLLECTIONS, 'otherUsersPrivate'],
    queryFn: getOtherUsersPrivateMCellCollections,
    enabled,
    staleTime: REFERENCE_DATA_STALE_TIME,
    meta: { errorMessageKey: 'errorFetchingWorkCellGroups' },
  });

const MCELL_COLLECTIONS_KEY = [QueryKeys.MCELL_COLLECTIONS];

/** Creates a private MCell collection. */
export const useCreateMCellCollectionMutation = () =>
  useInvalidatingMutation(
    MCELL_COLLECTIONS_KEY,
    createMCellCollection,
    'errorCreatingWorkCellGroup',
  );

/** Creates a global MCell collection. */
export const useCreateMCellCollectionGlobalMutation = () =>
  useInvalidatingMutation(
    MCELL_COLLECTIONS_KEY,
    createMCellCollectionGlobal,
    'errorCreatingWorkCellGroupGlobal',
  );

/** Makes an MCell collection global. */
export const useSetMCellCollectionGlobalMutation = () =>
  useInvalidatingMutation(
    MCELL_COLLECTIONS_KEY,
    setMCellCollectionGlobal,
    'errorSettingWorkCellGroupGlobal',
  );

/** Makes an MCell collection private. */
export const useSetMCellCollectionPrivateMutation = () =>
  useInvalidatingMutation(
    MCELL_COLLECTIONS_KEY,
    setMCellCollectionPrivate,
    'errorSettingWorkCellGroupPrivate',
  );

/** Renames an MCell collection. */
export const useRenameMCellCollectionMutation = () =>
  useInvalidatingMutation(
    MCELL_COLLECTIONS_KEY,
    renameMCellCollection,
    'errorRenamingWorkCellGroup',
  );

/** Replaces the MCells of an MCell collection. */
export const useSetMCellsOnMCellCollectionMutation = () =>
  useInvalidatingMutation(
    MCELL_COLLECTIONS_KEY,
    setMCellsOnMCellCollection,
    'errorSettingWorkCellsOnWorkCellGroup',
  );

/** Copies a global MCell collection to a new private one, which it returns. */
export const useCopyMCellCollectionToPrivateMutation = () =>
  useInvalidatingMutation(
    MCELL_COLLECTIONS_KEY,
    copyMCellCollectionToPrivate,
    'errorCopyingWorkCellGroupToPrivate',
  );

/** Deletes an MCell collection. */
export const useDeleteMCellCollectionMutation = () =>
  useInvalidatingMutation(
    MCELL_COLLECTIONS_KEY,
    deleteMCellCollection,
    'errorDeletingWorkCellGroup',
  );

/**
 * Custom hook to fetch an MCell by its ID.
 *
 * @param {string} id - The ID of the MCell to be fetched.
 * @param {boolean} [enabled=true] - Optional. Determines whether the query should be enabled.
 * @returns {UseQueryResult<MCell>} The result object from `useQuery`.
 */
export const useMCellByIdQuery = (
  id: string,
  enabled: boolean = true,
): UseQueryResult<MCell> => {
  return useQuery<MCell>({
    queryKey: [QueryKeys.MCELL, id],
    queryFn: () => getMCellById(id),
    enabled,
  });
};

/**
 * Custom hook to create a new MCell.
 *
 * @param {UseMutationOptions<MCell, Error, MCellInfo>} [options] - Optional. Mutation options.
 * @returns The result object from `useMutation`.
 */
export const useCreateMCell = (
  options?: UseMutationOptions<MCell, Error, MCellInfo>,
) => {
  const queryClient = useQueryClient();

  return useMutation<MCell, Error, MCellInfo>({
    mutationFn: createMCell,
    // Resolves once the MCell lists include the new MCell
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [QueryKeys.MCELLS] }),
    ...options,
  });
};

/**
 * Custom hook to fetch the clone previews (per-aspect counts + suggested code) for a selection of source MCells
 * in one request.
 *
 * @param {Array<string>} sourceMCellIds - The IDs of the MCells to clone from.
 * @param {boolean} [enabled=true] - Optional. Determines whether the query should be enabled.
 * @returns {UseQueryResult<Array<MCellClonePreview>>} The result object from `useQuery`.
 */
export const useMCellClonePreviewsQuery = (
  sourceMCellIds: Array<string>,
  enabled: boolean = true,
): UseQueryResult<Array<MCellClonePreview>> => {
  const sortedIds = [...sourceMCellIds].sort();
  return useQuery<Array<MCellClonePreview>>({
    queryKey: [QueryKeys.MCELL_CLONE_PREVIEW, sortedIds],
    queryFn: () => getMCellClonePreviews(sortedIds),
    enabled: enabled && sortedIds.length > 0,
  });
};

/**
 * Custom hook to clone MCells in one all-or-nothing batch (one request per new MCell).
 *
 * @param {UseMutationOptions<Array<MCellCloneResult>, Error, Array<MCellCloneCreate>>} [options] - Optional. Mutation options.
 * @returns The result object from `useMutation`.
 */
export const useCloneMCells = (
  options?: UseMutationOptions<
    Array<MCellCloneResult>,
    Error,
    Array<MCellCloneCreate>
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<Array<MCellCloneResult>, Error, Array<MCellCloneCreate>>({
    mutationFn: cloneMCells,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_COMPLETION_CONFIGS],
      });
      // The clone can join collections and support hierarchies, and changes what a future clone of the
      // same source would copy
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_COLLECTIONS],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_SUPPORT_HIERARCHY],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_CLONE_PREVIEW],
      });
      // The clone can re-home employees, so the lists that group them by work cell are stale
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_EMPLOYEES_LIST],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_EMPLOYEES_IN_MCELL],
      });
      // The clone can copy the source's crewing capacities
      queryClient.invalidateQueries({ queryKey: [QueryKeys.MCELL_CAPACITIES] });
      // Resolves once the MCell lists include the new MCells
      return queryClient.invalidateQueries({ queryKey: [QueryKeys.MCELLS] });
    },
    ...options,
  });
};

/**
 * Custom hook to update an existing MCell.
 *
 * @param {UseMutationOptions<MCell, Error, { id: string; info: MCellInfo }>} [options] - Optional. Mutation options.
 * @returns The result object from `useMutation`.
 */
export const useUpdateMCell = (
  options?: UseMutationOptions<MCell, Error, { id: string; info: MCellInfo }>,
) => {
  const queryClient = useQueryClient();

  return useMutation<MCell, Error, { id: string; info: MCellInfo }>({
    mutationFn: updateMCell,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.MCELLS] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL, variables.id],
      });
      // Collections embed their MCells
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_COLLECTIONS],
      });
    },
    ...options,
  });
};

/**
 * Custom hook to delete an MCell by its ID.
 *
 * @param {UseMutationOptions<string, Error, string>} [options] - Optional. Mutation options.
 * @returns The result object from `useMutation`.
 */
export const useDeleteMCell = (
  options?: UseMutationOptions<string, Error, string>,
) => {
  const queryClient = useQueryClient();

  return useMutation<string, Error, string>({
    mutationFn: deleteMCell,
    onSuccess: (data, mCellId) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.MCELLS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.MCELL, mCellId] });
      // Collections embed their MCells
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_COLLECTIONS],
      });
    },
    ...options,
  });
};

/**
 * Hook to get MCell Completion Configs by MCell Id
 */
export const useMCellCompletionConfigsByMCellIdQuery = (
  mCellId: string,
  enabled: boolean = true,
): UseQueryResult<MCellCompletionConfigDto[]> => {
  return useQuery<MCellCompletionConfigDto[]>({
    queryKey: [QueryKeys.MCELL_COMPLETION_CONFIGS, mCellId],
    queryFn: () => getMCellCompletionConfigsByMCellId(mCellId),
    enabled,
  });
};

/**
 * Hook to create MCell Completion Config
 */
export const useCreateMCellCompletionConfig = (
  options?: UseMutationOptions<
    MCellCompletionConfigDto,
    Error,
    MCellCompletionConfigCreate
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<
    MCellCompletionConfigDto,
    Error,
    MCellCompletionConfigCreate
  >({
    mutationFn: createMCellCompletionConfig,
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_COMPLETION_CONFIGS, vars.mCellId],
      });
    },
    ...options,
  });
};

/**
 * Hook to update MCell Completion Config
 */
export const useUpdateMCellCompletionConfig = (
  options?: UseMutationOptions<
    MCellCompletionConfigDto,
    Error,
    { id: string; configUpdate: MCellCompletionConfigUpdate }
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<
    MCellCompletionConfigDto,
    Error,
    { id: string; configUpdate: MCellCompletionConfigUpdate }
  >({
    mutationFn: updateMCellCompletionConfig,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_COMPLETION_CONFIGS],
      });
    },
    ...options,
  });
};

/**
 * Hook to delete MCell Completion Config
 */
export const useDeleteMCellCompletionConfig = (
  options?: UseMutationOptions<void, Error, { id: string; mCellId: string }>,
) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { id: string; mCellId: string }>({
    mutationFn: async ({ id }) => {
      await deleteMCellCompletionConfig(id);
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MCELL_COMPLETION_CONFIGS, vars.mCellId],
      });
    },
    ...options,
  });
};

/**
 * Hook to get Producing Location Options (by inventoryCenterId)
 */
export const useProducingLocationOptionsQuery = (
  inventoryCenterId: string | undefined,
  enabled: boolean = true,
): UseQueryResult<OptionValue<string>[]> => {
  return useQuery<OptionValue<string>[]>({
    queryKey: [QueryKeys.PRODUCING_LOCATIONS_OPTIONS, inventoryCenterId],
    queryFn: () => getProducingLocationOptions(inventoryCenterId!),
    enabled: enabled && !!inventoryCenterId,
  });
};

/**
 * Hook to get Production Line Options (by producingLocationId)
 */
export const useProductionLineOptionsQuery = (
  producingLocationId: string | undefined,
  enabled: boolean = true,
): UseQueryResult<OptionValue<string>[]> => {
  return useQuery<OptionValue<string>[]>({
    queryKey: [QueryKeys.PRODUCTION_LINES_OPTIONS, producingLocationId],
    queryFn: () => getProductionLineOptions(producingLocationId!),
    enabled: enabled && !!producingLocationId,
  });
};

/**
 * Hook to get Work Center Options (by producingLocationId)
 */
export const useWorkCenterOptionsQuery = (
  producingLocationId: string | undefined,
  enabled: boolean = true,
): UseQueryResult<OptionValue<string>[]> => {
  return useQuery<OptionValue<string>[]>({
    queryKey: [QueryKeys.WORK_CENTERS_OPTIONS, producingLocationId],
    queryFn: () => getWorkCenterOptions(producingLocationId!),
    enabled: enabled && !!producingLocationId,
  });
};

/**
 * Hook to get Work Center Station Options (by workCenterId)
 */
export const useWorkCenterStationOptionsQuery = (
  workCenterId: string | undefined,
  enabled: boolean = true,
): UseQueryResult<OptionValue<string>[]> => {
  return useQuery<OptionValue<string>[]>({
    queryKey: [QueryKeys.WORK_CENTER_STATIONS_OPTIONS, workCenterId],
    queryFn: () => getWorkCenterStationOptions(workCenterId!),
    enabled: enabled && !!workCenterId,
  });
};

/**
 * Query options of the Inventory Center list, for prefetching or reading it outside components.
 * @param includeDisabled - If true (default), disabled centers are included.
 */
export const inventoryCentersQueryOptions = (includeDisabled: boolean = true) =>
  queryOptions({
    queryKey: [QueryKeys.INVENTORY_CENTERS, includeDisabled],
    queryFn: () => getInventoryCenters({ includeDisabled }),
    staleTime: REFERENCE_DATA_STALE_TIME,
    meta: { errorMessageKey: 'errorFetchingManufacturingLocations' },
  });

/**
 * Hook to fetch the list of Inventory Centers.
 *
 * @param includeDisabled - If true (default), disabled centers are included.
 * @param enabled - Whether the query is enabled. Defaults to true.
 * @param select - Optional selector over the centers, e.g. to keep enabled ones only.
 */
export const useInventoryCentersQuery = <T = InventoryCenter[]>(
  includeDisabled: boolean = true,
  enabled: boolean = true,
  select?: (inventoryCenters: InventoryCenter[]) => T,
) =>
  useQuery({
    ...inventoryCentersQueryOptions(includeDisabled),
    enabled,
    select,
  });

/**
 * Hook to update an Inventory Center's configuration options (displayName, enabled).
 *
 * On success, invalidates the inventory centers and the MCells, which embed their inventory center.
 */
export const useSetInventoryCenterConfigOptionsMutation = (
  options?: UseMutationOptions<
    InventoryCenter,
    Error,
    {
      inventoryCenterId: string;
      inventoryCenterConfigOptions: InventoryCenterConfigOptions;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    InventoryCenter,
    Error,
    {
      inventoryCenterId: string;
      inventoryCenterConfigOptions: InventoryCenterConfigOptions;
    }
  >({
    mutationFn: setInventoryCenterConfigOptions,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.INVENTORY_CENTERS],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.MCELLS] });
    },
    ...options,
  });
};

/**
 * Hook to update an Inventory Center's SharePoint documentation-link configuration.
 *
 * Companion to {@link useSetInventoryCenterConfigOptionsMutation}; both target the same
 * entity but different endpoints with different authorization. On success, invalidates the
 * `INVENTORY_CENTERS` query (all variants of the `includeDisabled` key) so any UI rendering
 * inventory-center state refreshes.
 */
export const useUpdateInventoryCenterSharepointConfigMutation = (
  options?: UseMutationOptions<
    void,
    Error,
    {
      inventoryCenterId: string;
      input: InventoryCenterSharepointConfigInput;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    {
      inventoryCenterId: string;
      input: InventoryCenterSharepointConfigInput;
    }
  >({
    mutationFn: updateInventoryCenterSharepointConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.INVENTORY_CENTERS],
        exact: false,
      });
    },
    ...options,
  });
};

/**
 * Fetches the inventory center IDs a manufacturing location covers.
 * @param manufacturingLocation - The selected inventory center, MCell and MCell collection.
 * @param enabled - Whether the query is enabled. Defaults to true.
 */
export const useInventoryCenterIdsByManufacturingLocationQuery = (
  manufacturingLocation: ManufacturingLocation,
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: [
      QueryKeys.INVENTORY_CENTER_IDS_BY_MANUFACTURING_LOCATION,
      manufacturingLocation,
    ],
    queryFn: () =>
      getInventoryCenterIdsByManufacturingLocation(manufacturingLocation),
    enabled,
    meta: {
      errorMessageKey: 'errorFetchingInventoryCenterIdsByManufacturingLocation',
    },
  });

/**
 * Fetches the Producing Locations of an inventory center.
 * @param inventoryCenterId
 */
export const useProducingLocationsQuery = (inventoryCenterId: string) =>
  useQuery({
    queryKey: [QueryKeys.PRODUCING_LOCATIONS, inventoryCenterId],
    queryFn: () => getProducingLocations(inventoryCenterId),
    meta: { errorMessageKey: 'errorFetchingProducingLocations' },
  });

/**
 * Fetches the Planning Departments of an inventory center.
 * @param filter - The inventory center, and whether to return departments of interest only.
 * @param enabled - Whether the query is enabled. Defaults to true.
 */
export const usePlanningDepartmentsQuery = (
  filter: {
    inventoryCenterId?: string;
    departmentsOfInterestOnly?: boolean;
  },
  enabled = true,
) =>
  useQuery({
    queryKey: [QueryKeys.PLANNING_DEPARTMENTS, filter],
    queryFn: () => getPlanningDepartments(filter),
    enabled,
    meta: { errorMessageKey: 'errorFetchingPlanningDepartments' },
  });

/**
 * Sets whether a Planning Department is of interest, patching the cached lists right away.
 */
export const useUpdatePlanningDepartmentOfInterestMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.PLANNING_DEPARTMENTS],
    updatePlanningDepartment,
    'errorUpdatingPlanningDepartment',
    (departments: PlanningDepartment[], { id, isDepartmentOfInterest }) =>
      departments.map((d) =>
        d.id === id ? { ...d, isDepartmentOfInterest } : d,
      ),
  );

/**
 * Fetches the Key MCell infos of an inventory center.
 * @param inventoryCenterId
 */
export const useKeyMCellsQuery = (inventoryCenterId: string) =>
  useQuery({
    queryKey: [QueryKeys.KEY_MCELLS, inventoryCenterId],
    queryFn: () => getKeyMCells(inventoryCenterId),
    meta: { errorMessageKey: 'errorFetchingKeyWorkCells' },
  });

/**
 * Updates a Key MCell info, patching the cached lists right away.
 */
export const useUpdateKeyMCellMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.KEY_MCELLS],
    updateKeyMCell,
    'errorUpdatingKeyWorkCell',
    (keyMCells: KeyMCell[], update: KeyMCellUpdate) =>
      keyMCells.map((k) =>
        k.mCellId === update.mCellId ? { ...k, ...update } : k,
      ),
  );

/**
 * Fetches the Notification System of an inventory center, or null when none is set.
 * @param inventoryCenterId
 */
export const useNotificationSystemQuery = (inventoryCenterId: string) =>
  useQuery({
    queryKey: [QueryKeys.NOTIFICATION_SYSTEM, inventoryCenterId],
    queryFn: async () =>
      (await getNotificationSystem(inventoryCenterId)) ?? null,
    meta: { errorMessageKey: 'errorFetchingNotificationSystem' },
  });

/**
 * Sets the Notification System of an inventory center.
 */
export const useSetNotificationSystemMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.NOTIFICATION_SYSTEM],
    setNotificationSystem,
    'errorUpdatingNotificationSystem',
  );
